import React, { useEffect, useMemo, useState } from 'react';
import useDialogDismiss from '../../hooks/useDialogDismiss';
import { X, Check, Lightbulb } from 'lucide-react';
import { TYPE } from '../../constants/typeScale';
import { flowOverlay, flowPanel } from '../ui/modalSpacing';
import Tooltip from '../ui/Tooltip';

/**
 * THE PLOT LIBRARY — pick a starting story, in a modal (owner 2026-08-23).
 *
 * Two owner rules meet here. The specific one: *"I don't like ideas as dropdown, just have a lightbulb, that
 * is a modal for select template, have more templates of various categories."* The general one, stated in the
 * same breath: *"in general, I don't like drop down controls, modal for selecting is better"* — which is the
 * same law that already governs agent picking (`AgentHiringModal`). A dropdown shows a list through a
 * letterbox: it can hold six items, truncates the one line that says what each plot IS, and opens over the
 * page it is meant to inform. A modal can hold the whole catalog, shelved, with each premise readable.
 *
 * Presentational only (SteveJobs layering): shelves in, a key out. The catalog itself is server-owned
 * (`functions/src/agents/microDramaGenres.ts`) so the beat guidance behind each plot never reaches the client.
 */

export interface StoryTemplate {
  key: string;
  label: string;
  premise: string;
  /** The shelf this sits on. Older payloads may not carry one — those fall into a single unnamed shelf. */
  category?: string;
}

export interface TemplateModalProps {
  open: boolean;
  onClose: () => void;
  templates: StoryTemplate[];
  /** Which plot is currently seeding the idea, if any. */
  templateKey?: string;
  onPick: (key: string) => void;
}

const OTHER = 'More';

/** Shelves in catalog order — the server orders them by what the market pays for, so do not re-sort. */
function shelve(templates: StoryTemplate[]): Array<{ name: string; items: StoryTemplate[] }> {
  const order: string[] = [];
  const by = new Map<string, StoryTemplate[]>();
  for (const t of templates) {
    const name = t.category || OTHER;
    if (!by.has(name)) { by.set(name, []); order.push(name); }
    by.get(name)!.push(t);
  }
  return order.map((name) => ({ name, items: by.get(name)! }));
}

const TemplateModal: React.FC<TemplateModalProps> = ({ open, onClose, templates, templateKey, onPick }) => {
  // Escape + focus, shared with every other dialog. The hand-rolled `onKeyDown` below never fired: nothing
  // moved focus into the modal, so the key was delivered to the page behind it (BUGS.md 2026-08-30).
  const dialog = useDialogDismiss(open, onClose);
  const shelves = useMemo(() => shelve(templates), [templates]);
  const [shelf, setShelf] = useState(0);

  // Open on the shelf holding the current pick, so "what did I choose?" is answered without hunting.
  useEffect(() => {
    if (!open) return;
    const at = templateKey ? shelves.findIndex((s) => s.items.some((t) => t.key === templateKey)) : 0;
    setShelf(at >= 0 ? at : 0);
  }, [open, templateKey, shelves]);

  if (!open) return null;
  const active = shelves[Math.min(shelf, Math.max(shelves.length - 1, 0))];

  return (
    <div
      className={`${flowOverlay} z-50`}
      {...dialog}
      role="dialog"
      aria-modal="true"
      aria-label="Start from a plot"
      data-testid="template-modal"
    >
      <div className={`${flowPanel} sm:max-w-3xl sm:h-auto sm:max-h-[85dvh]`}>
        <div className="flex shrink-0 items-center gap-2 border-b border-secondary-200 px-4 py-3 dark:border-secondary-600">
          <Lightbulb className="h-4 w-4 shrink-0 text-secondary-400" aria-hidden />
          <h2 className={`min-w-0 flex-1 ${TYPE.stageTitle} text-secondary-900 dark:text-white`}>Start from a plot</h2>
          <Tooltip content="Close" side="bottom">
            <button
              type="button" onClick={onClose} aria-label="Close"
              className="shrink-0 rounded-lg p-1 text-secondary-400 hover:bg-secondary-100 hover:text-secondary-700
                         dark:hover:bg-secondary-800 dark:hover:text-secondary-200"
            >
              <X className="h-4 w-4" />
            </button>
          </Tooltip>
        </div>

        {/* THE SHELVES. A tablist, not a second dropdown — every shelf name is visible at once, which is the
            whole reason this stopped being a menu. Horizontally scrollable so a new shelf never wraps the row. */}
        <div
          role="tablist" aria-label="Plot categories"
          className="flex shrink-0 gap-1 overflow-x-auto border-b border-secondary-200 px-3 py-2
                     [-ms-overflow-style:none] [scrollbar-width:none] dark:border-secondary-600
                     [&::-webkit-scrollbar]:hidden"
        >
          {shelves.map((s, i) => (
            <button
              key={s.name}
              type="button" role="tab" aria-selected={i === shelf}
              onClick={() => setShelf(i)}
              className={`shrink-0 rounded-lg px-2.5 py-1 ${TYPE.control} transition-colors ${
                i === shelf
                  ? 'bg-secondary-900 font-medium text-white dark:bg-white dark:text-secondary-900'
                  : 'text-secondary-500 hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-800'}`}
            >
              {s.name}
            </button>
          ))}
        </div>

        {/* Two columns of plots. The premise is the point of the card, so it is never truncated to one line —
            that clipping is exactly what the dropdown did and why it could not be browsed. */}
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          <div className="grid gap-2 sm:grid-cols-2">
            {(active?.items || []).map((t) => {
              const on = t.key === templateKey;
              return (
                <button
                  key={t.key}
                  type="button"
                  data-testid={`template-${t.key}`}
                  onClick={() => { onPick(t.key); onClose(); }}
                  className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-colors ${
                    on
                      ? 'border-secondary-900 bg-secondary-50 dark:border-white dark:bg-secondary-800'
                      : 'border-secondary-200 hover:border-secondary-400 dark:border-secondary-600 dark:hover:border-secondary-500'}`}
                >
                  <span className="min-w-0 flex-1">
                    <span className={`block ${TYPE.sectionTitle} text-secondary-900 dark:text-white`}>{t.label}</span>
                    <span className={`mt-1 block ${TYPE.control} leading-relaxed text-secondary-500 dark:text-secondary-400`}>
                      {t.premise}
                    </span>
                  </span>
                  {on && <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-secondary-900 dark:text-white" aria-hidden />}
                </button>
              );
            })}
          </div>
        </div>

        {/* A plot is a starting point, never a lock-in — so the way out of one is on the same surface. */}
        <div className="flex shrink-0 items-center justify-between gap-3 border-t border-secondary-200 px-4 py-2.5 dark:border-secondary-600">
          <span className={`${TYPE.meta} text-secondary-400`}>Every plot is editable once it lands in your idea.</span>
          <button
            type="button"
            onClick={() => { onPick(''); onClose(); }}
            className={`shrink-0 rounded-lg px-2.5 py-1 ${TYPE.control} text-secondary-500 transition-colors
                        hover:bg-secondary-100 dark:text-secondary-400 dark:hover:bg-secondary-800`}
          >
            Write my own instead
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateModal;
